/*
 * Resale certificate upload on the trade form (CM-84; docs/TRADECERT-CONTRACT.md
 * section 2; markup in snippets/lp-trade-cert.liquid).
 *
 * Picking a file uploads it at once: a Turnstile token (the wishlist's widget,
 * shown only if Cloudflare needs a click), then POST {upload address}/v1/certs.
 * On 201 ONE hidden input goes into the form,
 *   name="contact[resale-certificate]"  value="LP-CERT-<id> | <clean filename>"
 * and the field shows the file name with Remove. Anything else is one short
 * line under the field; the form always sends (the field is optional).
 *
 * If Send is pressed while an upload is still running, sending waits for it
 * (at most HOLD_MAX_MS), then goes ahead through the form's own submit, so the
 * store's spam check still runs. Without a valid https upload address and a site
 * key the field stays hidden and nothing here runs.
 */
(function () {
  'use strict';

  var FIELD_NAME = 'contact[resale-certificate]';
  var REF_RE = /^LP-CERT-[A-Z2-7]{26}$/;
  var MAX_BYTES = 10 * 1024 * 1024;
  var TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif'];
  var EXT_RE = /\.(pdf|jpe?g|png|heic|heif)$/i;
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  var TURNSTILE_ACTION = 'trade-cert';
  var UPLOAD_TIMEOUT_MS = 120000;
  var HOLD_MAX_MS = 45000;

  /*
   * One record for the whole page, shared by every copy of this script: the theme
   * editor can run the section's script again, and each field must still be set up
   * once, by one copy (two copies would each bind it). `fields` holds the fields
   * already set up, `listening` says the editor's section-reload listener is in
   * place, `turnstile` is the one loading of Cloudflare's script.
   */
  var shared = window.__lpTradeCert || (window.__lpTradeCert = {
    fields: typeof WeakSet === 'function' ? new WeakSet() : null,
    listening: false,
    turnstile: null
  });

  /** The upload address as an https origin plus path, without a trailing slash or /v1/certs; null when unusable. */
  function apiBase(raw) {
    try {
      var url = new URL(String(raw || '').trim());
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return null;
      return url.origin + url.pathname.replace(/\/+$/, '').replace(/\/v1\/certs$/, '');
    } catch (err) {
      return null;
    }
  }

  function formatSize(bytes) {
    if (!(bytes > 0)) return '';
    if (bytes < 1000000) return Math.max(1, Math.round(bytes / 1000)) + ' KB';
    return (bytes / 1000000).toFixed(1) + ' MB';
  }

  /** Cloudflare's Turnstile API: the one already on the page, else loaded once. Resolves null when it cannot load. */
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (shared.turnstile) return shared.turnstile;
    shared.turnstile = new Promise(function (resolve) {
      var script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', function () { resolve(window.turnstile || null); });
      script.addEventListener('error', function () { resolve(null); });
      (document.head || document.documentElement).appendChild(script);
    }).then(function (api) {
      if (!api) shared.turnstile = null;
      return api;
    });
    return shared.turnstile;
  }

  function Field(root) {
    this.root = root;
    this.api = apiBase(root.getAttribute('data-api'));
    this.sitekey = (root.getAttribute('data-sitekey') || '').trim();
    this.form = root.closest('form');
    this.input = root.querySelector('[data-lp-cert-input]');
    if (!this.api || !this.sitekey || !this.form || !this.input) return;

    var attr = function (name) { return root.getAttribute('data-' + name) || ''; };
    this.copy = {
      choose: attr('choose'),
      empty: attr('empty'),
      uploading: attr('uploading'),
      uploaded: attr('uploaded'),
      removed: attr('removed'),
      tooLarge: attr('msg-too-large'),
      badType: attr('msg-bad-type'),
      rateLimited: attr('msg-rate-limited'),
      failed: attr('msg-failed'),
      holding: attr('msg-holding')
    };
    this.control = root.querySelector('.lp-cert__control');
    this.nameEl = root.querySelector('[data-lp-cert-name]');
    this.metaEl = root.querySelector('[data-lp-cert-meta]');
    this.actionEl = root.querySelector('[data-lp-cert-action]');
    this.removeEl = root.querySelector('[data-lp-cert-remove]');
    this.fillEl = root.querySelector('[data-lp-cert-fill]');
    this.checkEl = root.querySelector('[data-lp-cert-check]');
    this.statusEl = root.querySelector('[data-lp-cert-status]');

    this.attached = null; // { ref, name, size } of the file the hidden input names
    this.job = null; // the upload in flight
    this.hold = null; // a held submit
    this.widgetId = null;
    this.waiter = null; // the token request in flight
    this.note = null;

    var self = this;
    this.input.addEventListener('change', function () {
      var file = self.input.files && self.input.files[0];
      self.input.value = '';
      self.pick(file);
    });
    this.removeEl.addEventListener('click', function () { self.remove(); });
    var warm = function () { loadTurnstile(); };
    this.control.addEventListener('pointerenter', warm, { once: true });
    this.control.addEventListener('focusin', warm, { once: true });
    this.control.addEventListener('touchstart', warm, { once: true, passive: true });
    this.control.addEventListener('dragenter', function () { root.classList.add('is-drag'); });
    this.control.addEventListener('dragleave', function () { root.classList.remove('is-drag'); });
    this.control.addEventListener('drop', function () { root.classList.remove('is-drag'); });
    // Capture: runs before the form's other submit handlers (the store's spam check among them).
    this.form.addEventListener('submit', function (event) { self.onSubmit(event); }, true);

    this.render('empty');
    root.hidden = false;
    var column = root.closest('[data-lp-cert-col]');
    if (column) column.hidden = false;
    this.ready = true;
  }

  Field.prototype.pick = function (file) {
    if (!file) return; // the picker was closed: keep what is attached
    this.say('');
    if (file.size > MAX_BYTES) return this.say(this.copy.tooLarge, true);
    var type = String(file.type || '').toLowerCase();
    if (type && TYPES.indexOf(type) < 0 && !EXT_RE.test(file.name || '')) return this.say(this.copy.badType, true);
    if (file.size === 0) return this.say(this.copy.failed, true);
    this.start(file);
  };

  Field.prototype.start = function (file) {
    this.cancel();
    var job = { file: file, xhr: null };
    this.job = job;
    this.render('uploading', file.name, file.size);
    this.say(this.copy.uploading + ': ' + file.name, false, true);
    var self = this;
    this.token().then(function (token) {
      if (self.job === job) self.send(job, token);
    }, function () {
      if (self.job === job) self.finish(job, { error: 'failed' });
    });
  };

  /** A fresh, single-use Turnstile token for this upload. */
  Field.prototype.token = function () {
    var self = this;
    return loadTurnstile().then(function (ts) {
      if (!ts) throw new Error('turnstile unavailable');
      return new Promise(function (resolve, reject) {
        self.waiter = { resolve: resolve, reject: reject };
        try {
          if (self.widgetId === null || self.widgetId === undefined) {
            self.widgetId = ts.render(self.checkEl, {
              sitekey: self.sitekey,
              action: TURNSTILE_ACTION,
              appearance: 'interaction-only',
              execution: 'execute',
              size: 'flexible',
              theme: 'light',
              retry: 'never',
              'refresh-expired': 'manual',
              'response-field': false,
              callback: function (token) { self.settleToken(token); },
              'error-callback': function () { self.settleToken(null); return true; },
              'expired-callback': function () { self.settleToken(null); },
              'timeout-callback': function () { self.settleToken(null); },
              'unsupported-callback': function () { self.settleToken(null); },
              'before-interactive-callback': function () { self.checkEl.classList.add('is-interactive'); },
              'after-interactive-callback': function () { self.checkEl.classList.remove('is-interactive'); }
            });
          }
          ts.execute(self.checkEl);
        } catch (err) {
          self.settleToken(null);
        }
      });
    });
  };

  Field.prototype.settleToken = function (token) {
    var waiter = this.waiter;
    this.waiter = null;
    if (!waiter) return;
    if (token) waiter.resolve(token);
    else waiter.reject(new Error('no token'));
  };

  /** Ready for the next upload: a token is spent once, and an unfinished check is dropped. */
  Field.prototype.resetCheck = function () {
    this.settleToken(null);
    this.checkEl.classList.remove('is-interactive');
    if (this.widgetId !== null && window.turnstile && typeof window.turnstile.reset === 'function') {
      try { window.turnstile.reset(this.widgetId); } catch (err) { /* the next execute asks again */ }
    }
  };

  Field.prototype.send = function (job, token) {
    var self = this;
    var xhr = new XMLHttpRequest();
    job.xhr = xhr;
    var body = new FormData();
    body.append('file', job.file, job.file.name);
    body.append('turnstile', token);
    xhr.open('POST', this.api + '/v1/certs', true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.upload.addEventListener('progress', function (event) {
      if (self.job === job && event.lengthComputable && event.total > 0) self.progress(event.loaded / event.total);
    });
    xhr.addEventListener('load', function () { self.finish(job, self.read(xhr)); });
    xhr.addEventListener('error', function () { self.finish(job, { error: 'failed' }); });
    xhr.addEventListener('timeout', function () { self.finish(job, { error: 'failed' }); });
    xhr.send(body);
  };

  /**
   * The Worker's answer: { ok: {ref, name, size} } for a good 201, else { error: code }. The name
   * goes into the store email, so one with a pipe, a colon or a control character is refused
   * (the contract's clean name never has them).
   */
  Field.prototype.read = function (xhr) {
    var body = null;
    try { body = JSON.parse(xhr.responseText); } catch (err) { body = null; }
    if (xhr.status === 201 && body && typeof body.ref === 'string' && REF_RE.test(body.ref) &&
        typeof body.name === 'string' && body.name && body.name.length <= 100 && !/[|:\u0000-\u001f\u007f]/.test(body.name)) {
      return { ok: { ref: body.ref, name: body.name, size: typeof body.size === 'number' ? body.size : 0 } };
    }
    return { error: body && typeof body.error === 'string' ? body.error : 'failed' };
  };

  Field.prototype.finish = function (job, result) {
    if (this.job !== job) return;
    this.job = null;
    this.resetCheck();
    if (result.ok) {
      this.attach(result.ok);
      this.render('done', result.ok.name, result.ok.size);
      this.say(this.copy.uploaded + ': ' + result.ok.name, false, true);
    } else {
      this.restore();
      this.say(this.message(result.error), true);
    }
    this.release();
  };

  Field.prototype.message = function (code) {
    if (code === 'too_large') return this.copy.tooLarge;
    if (code === 'bad_type') return this.copy.badType;
    if (code === 'rate_limited') return this.copy.rateLimited;
    return this.copy.failed;
  };

  /** The one hidden input the contract names; any earlier one goes first. */
  Field.prototype.attach = function (cert) {
    this.detach();
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = FIELD_NAME;
    input.value = cert.ref + ' | ' + cert.name;
    this.root.appendChild(input);
    this.attached = cert;
  };

  Field.prototype.detach = function () {
    var olds = this.form.querySelectorAll('input[name="' + FIELD_NAME + '"]');
    for (var i = 0; i < olds.length; i++) olds[i].parentNode.removeChild(olds[i]);
    this.attached = null;
  };

  /** Back to what the hidden input names: the earlier file, or nothing. */
  Field.prototype.restore = function () {
    if (this.attached) this.render('done', this.attached.name, this.attached.size);
    else this.render('empty');
  };

  Field.prototype.remove = function () {
    this.cancel();
    this.detach();
    this.render('empty');
    this.say(this.copy.removed, false, true);
    try { this.input.focus({ preventScroll: true }); } catch (err) { this.input.focus(); }
  };

  /** Stops the upload in flight, if any; its answer is then ignored. */
  Field.prototype.cancel = function () {
    var job = this.job;
    this.job = null;
    if (job && job.xhr) {
      try { job.xhr.abort(); } catch (err) { /* already finished */ }
    }
    if (job) this.resetCheck();
  };

  Field.prototype.render = function (state, name, size) {
    this.root.setAttribute('data-state', state);
    this.nameEl.textContent = state === 'empty' ? this.copy.empty : name;
    this.metaEl.textContent = state === 'empty' ? '' : formatSize(size);
    this.actionEl.textContent = state === 'uploading' ? this.copy.uploading + '…' : this.copy.choose;
    this.removeEl.hidden = state !== 'done';
    this.removeEl.setAttribute('aria-label', state === 'done' ? this.removeEl.textContent.trim() + ' ' + name : this.removeEl.textContent.trim());
    this.progress(0);
  };

  /** The real fill while the file goes up; at 0 or 1 (waiting for the check or the server) the slow sweep shows instead. */
  Field.prototype.progress = function (fraction) {
    var f = Math.max(0, Math.min(1, fraction));
    this.fillEl.style.width = Math.round(f * 100) + '%';
    this.root.classList.toggle('has-progress', f > 0 && f < 1);
  };

  /** One line under the field: an error shows (and is read out); anything else is only read out. */
  Field.prototype.say = function (text, isError, quiet) {
    this.statusEl.textContent = text || '';
    this.statusEl.classList.toggle('is-quiet', !!quiet && !isError);
  };

  /** Send pressed during an upload: wait for it, then send through the form's own submit. */
  Field.prototype.onSubmit = function (event) {
    if (!this.job || typeof this.form.requestSubmit !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hold) return;
    var self = this;
    this.hold = {
      submitter: event.submitter || null,
      timer: window.setTimeout(function () {
        self.cancel();
        self.restore();
        self.release();
      }, HOLD_MAX_MS)
    };
    this.showNote(this.copy.holding);
  };

  Field.prototype.release = function () {
    var hold = this.hold;
    if (!hold) return;
    this.hold = null;
    window.clearTimeout(hold.timer);
    this.showNote('');
    var form = this.form;
    var submitter = hold.submitter && hold.submitter.form === form ? hold.submitter : null;
    window.setTimeout(function () {
      try {
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } catch (err) {
        form.submit();
      }
    }, 0);
  };

  /** The line under the Send button, made once, next to the form's submit button. */
  Field.prototype.showNote = function (text) {
    if (!this.note) {
      var button = this.form.querySelector('button[type="submit"], input[type="submit"]');
      if (!button) return;
      this.note = document.createElement('p');
      this.note.className = 'lp-cert-note';
      this.note.setAttribute('role', 'status');
      this.note.setAttribute('aria-live', 'polite');
      this.note.setAttribute('style', this.root.getAttribute('style') || '');
      this.note.hidden = true;
      button.insertAdjacentElement('afterend', this.note);
    }
    this.note.textContent = text;
    this.note.hidden = !text;
  };

  /** Sets up every field in `scope` that no copy of this script has set up yet. */
  function startAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-lp-cert]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (shared.fields ? shared.fields.has(root) : root.getAttribute('data-lp-cert-started')) continue;
      if (shared.fields) shared.fields.add(root);
      else root.setAttribute('data-lp-cert-started', '1');
      try { new Field(root); } catch (err) { /* the field stays hidden; the form is untouched */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { startAll(document); });
  } else {
    startAll(document);
  }
  // The theme editor's section reload sets up the new field: one listener for the page, whichever copy came first.
  if (!shared.listening) {
    shared.listening = true;
    document.addEventListener('shopify:section:load', function (event) { startAll(event.target); });
  }
})();
